import React, { useEffect, useMemo, useState } from "react";
import { warehouseInventoryService } from "../../../services/warehouseInventory.service";
import { warehouseCatalogService } from "../../../services/warehouseCatalog.service";
import InventoryHeader from "./components/InventoryHeader";
import InventoryTabs from "./components/InventoryTabs";
import StockSection from "./components/StockSection";
import StockAlertsSection from "./components/StockAlertsSection";
import ExpiredSection from "./components/ExpiredSection";
import OrdersSection from "./components/OrdersSection";
import InboundSection from "./components/InboundSection";
import CreateOrderModal from "./components/CreateOrderModal";
import OrderDetailModal from "./components/OrderDetailModal";
import ReceiptDetailModal from "./components/ReceiptDetailModal";
import { BORDER, PAGE_SIZE, INVENTORY_TABS } from "./utils/inventory.constants";
import {
  getErrorMessage,
  processStockItems,
  toInputDate,
} from "./utils/inventory.helpers";

function normalizeLookupList(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.rows)) return value.rows;
  if (Array.isArray(value?.result)) return value.result;
  return [];
}

function getLookupId(item) {
  return item?.id ?? item?._id ?? item?.value ?? "";
}

function getLookupLabel(item) {
  return item?.name || item?.label || item?.slug || item?.title || getLookupId(item);
}

function getParentId(item) {
  return item?.parentId ?? item?.parent_id ?? item?.categoryId ?? item?.category_id ?? "";
}

function uniqueLookupList(items = []) {
  const seen = new Set();
  return normalizeLookupList(items).filter((item) => {
    const id = String(getLookupId(item) || "");
    const key = id || String(getLookupLabel(item) || "").toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function cleanText(value) {
  return String(value ?? "").trim().toLowerCase();
}

function valuesFromKeys(item = {}, keys = []) {
  return keys
    .flatMap((key) => {
      const value = item?.[key];
      if (value && typeof value === "object") {
        return [value?.id, value?._id, value?.name, value?.label].filter(
          (nested) => nested !== undefined && nested !== null && String(nested).trim() !== "",
        );
      }
      return [value];
    })
    .filter((value) => value !== undefined && value !== null && String(value).trim() !== "")
    .map((value) => String(value));
}

function matchesByIdsOrNames(item, { ids = [], names = [], idKeys = [], nameKeys = [] }) {
  const wantedIds = ids.map((id) => String(id)).filter(Boolean);
  const wantedNames = names.map(cleanText).filter(Boolean);

  if (!wantedIds.length && !wantedNames.length) return true;

  const itemIds = valuesFromKeys(item, idKeys);
  if (itemIds.length && itemIds.some((id) => wantedIds.includes(String(id)))) {
    return true;
  }

  const itemNames = valuesFromKeys(item, nameKeys).map(cleanText);
  if (itemNames.length && itemNames.some((name) => wantedNames.includes(name))) {
    return true;
  }

  return false;
}

function optionLabel(options = [], value) {
  return options.find((option) => String(option.value) === String(value))?.label || "";
}

function firstFilled(item = {}, keys = []) {
  for (const key of keys) {
    const value = item?.[key];
    if (value && typeof value === "object") {
      const nested = value?.id || value?._id || value?.name || value?.label || "";
      if (nested !== undefined && nested !== null && String(nested).trim() !== "") return nested;
      continue;
    }
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
}

function buildCategoryMeta(categories = []) {
  const normalized = uniqueLookupList(categories);
  const byId = new Map();
  normalized.forEach((item) => {
    const id = String(getLookupId(item) || "");
    if (id) byId.set(id, item);
  });
  return { categories: normalized, byId };
}


function categoryPath(categoryId, byId = new Map()) {
  const parts = [];
  const seen = new Set();
  let currentId = String(categoryId || "");

  while (currentId && !seen.has(currentId)) {
    seen.add(currentId);
    const item = byId.get(currentId);
    if (!item) break;
    parts.unshift(getLookupLabel(item));
    currentId = String(getParentId(item) || "");
  }

  return parts.filter(Boolean).join(" > ");
}

function collectCategoryDescendantIds(categories = [], rootId = "") {
  const root = String(rootId || "");
  if (!root) return [];

  const childrenByParent = new Map();
  for (const item of categories) {
    const id = String(getLookupId(item) || "");
    const parentId = String(getParentId(item) || "");
    if (!id) continue;
    if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
    childrenByParent.get(parentId).push(id);
  }

  const result = [];
  const seen = new Set();
  const queue = [root];
  while (queue.length) {
    const id = queue.shift();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
    queue.push(...(childrenByParent.get(id) || []));
  }
  return result;
}

function getItemCategoryId(item = {}) {
  return firstFilled(item, [
    "category_id",
    "categoryId",
    "category",
    "subcategory_id",
    "subCategoryId",
    "subcategoryId",
  ]);
}

function getItemCategoryParentId(item = {}, categoryById = new Map()) {
  const direct = firstFilled(item, [
    "category_parent_id",
    "categoryParentId",
    "parent_category_id",
    "parentCategoryId",
  ]);
  if (direct) return direct;

  const categoryId = String(getItemCategoryId(item) || "");
  const categoryItem = categoryById.get(categoryId);
  return categoryItem ? getParentId(categoryItem) : "";
}

function getUsedCategoryIds(items = [], categoryById = new Map()) {
  const used = new Set();

  items.forEach((item) => {
    const categoryId = String(getItemCategoryId(item) || "");
    const parentId = String(getItemCategoryParentId(item, categoryById) || "");
    if (categoryId) used.add(categoryId);
    if (parentId) used.add(parentId);
  });

  return used;
}

function getStockRowKey(item = {}) {
  return `${item?.product_id || item?.productId || ""}:${item?.product_variant_id || item?.productVariantId || item?.variant_id || item?.variantId || ""}`;
}

export default function WarehouseInventory({
  initialTab = "alerts",
  allowedTabs = INVENTORY_TABS,
  showTabs = true,
  pageTitle = "Warehouse Inventory",
  pageDescription = "Manage stock alerts, review expired batches separately, raise order requests to admin, and manage warehouse inventory operations.",
}) {
  const safeTabs = Array.isArray(allowedTabs) && allowedTabs.length ? allowedTabs : INVENTORY_TABS;
  const defaultTab = safeTabs.some((item) => item.key === initialTab)
    ? initialTab
    : safeTabs[0]?.key || "alerts";

  const [tab, setTab] = useState(defaultTab);

  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState("");

  const [stock, setStock] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stockQuery, setStockQuery] = useState("");
  const [debouncedStockQuery, setDebouncedStockQuery] = useState("");
  const [includeBatches, setIncludeBatches] = useState(false);
  const [onlyLow, setOnlyLow] = useState(false);
  const [onlyComingSoon, setOnlyComingSoon] = useState(false);
  const [stockCategory, setStockCategory] = useState("");
  const [stockSubcategory, setStockSubcategory] = useState("");
  const [stockBrand, setStockBrand] = useState("");
  const [stockLookups, setStockLookups] = useState({
    loading: false,
    error: "",
    categories: [],
    subcategories: [],
    brands: [],
  });

  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("");
  const [orderDetail, setOrderDetail] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [orderCreateOpen, setOrderCreateOpen] = useState(false);
  const [orderNotes, setOrderNotes] = useState("");
  const [orderDraftItems, setOrderDraftItems] = useState([{ productId: "", qty: 1 }]);
  const [orderCreateMessage, setOrderCreateMessage] = useState("");

  const [receipts, setReceipts] = useState([]);
  const [receiptFilter, setReceiptFilter] = useState("");
  const [receiptDetail, setReceiptDetail] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptMessage, setReceiptMessage] = useState("");
  const [receiptPopup, setReceiptPopup] = useState(null);
  const [receiving, setReceiving] = useState(false);
  const [receiveForm, setReceiveForm] = useState({ note: "", items: [] });
  const [expiredRows, setExpiredRows] = useState([]);
  const [masterProducts, setMasterProducts] = useState([]);

  const [stockPage, setStockPage] = useState(1);
  const [stockPageSize, setStockPageSize] = useState(10);
  const [stockDisplayStatusSavingKey, setStockDisplayStatusSavingKey] = useState("");
  const [stockZeroSavingKey, setStockZeroSavingKey] = useState("");
  const [alertsPage, setAlertsPage] = useState(1);
  const [expiredPage, setExpiredPage] = useState(1);
  const [ordersPage, setOrdersPage] = useState(1);
  const [receiptsPage, setReceiptsPage] = useState(1);

  useEffect(() => {
    const hasTab = safeTabs.some((item) => item.key === tab);
    if (!hasTab) {
      setTab(defaultTab);
    }
  }, [tab, defaultTab, safeTabs]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setStockLookups((prev) => ({ ...prev, loading: true, error: "" }));

        const [categoriesRes, brandsRes] = await Promise.all([
          warehouseCatalogService.getAllCategories
            ? warehouseCatalogService.getAllCategories()
            : warehouseCatalogService.getCategories(),
          warehouseCatalogService.getBrands(),
        ]);

        if (!alive) return;

        const categoryList = uniqueLookupList(categoriesRes);

        setStockLookups({
          loading: false,
          error: "",
          categories: categoryList,
          subcategories: categoryList.filter((item) => getParentId(item)),
          brands: uniqueLookupList(brandsRes),
        });
      } catch (err) {
        if (!alive) return;
        setStockLookups((prev) => ({
          ...prev,
          loading: false,
          error: getErrorMessage(err) || "Failed to load stock filters",
        }));
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    loadTabData(tab);
  }, [tab]);

  useEffect(() => {
    setStockPage(1);
  }, [stockQuery, includeBatches, onlyLow, onlyComingSoon, stockCategory, stockSubcategory, stockBrand, stock]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStockQuery(stockQuery);
    }, 400);

    return () => clearTimeout(timer);
  }, [stockQuery]);

  useEffect(() => {
    if (tab === "stock") {
      loadTabData("stock");
    }
  }, [tab, includeBatches]);

  useEffect(() => {
    if (tab === "inbound") {
      loadTabData("inbound");
    }
  }, [tab, receiptFilter]);

  useEffect(() => {
    setAlertsPage(1);
  }, [alerts]);

  useEffect(() => {
    setExpiredPage(1);
  }, [stockQuery, expiredRows]);

  useEffect(() => {
    setOrdersPage(1);
  }, [orderFilter, orders]);

  useEffect(() => {
    setReceiptsPage(1);
  }, [receiptFilter, receipts]);

  async function loadTabData(currentTab = tab) {
    try {
      setLoading(true);
      setPageError("");

      if (currentTab === "stock") {
        const [stockRes, alertRes] = await Promise.all([
          warehouseInventoryService.getStock({
            includeBatches,
            expiringDays: 30,
          }),
          warehouseInventoryService.getStockAlerts({ limit: 100 }),
        ]);

        // Stock API now returns category/brand metadata directly. Avoid downloading
        // every assigned product page before rendering the stock screen.
        setStock(stockRes?.items || []);
        setAlerts(alertRes?.items || []);
      }

      if (currentTab === "alerts") {
        const alertRes = await warehouseInventoryService.getStockAlerts({ limit: 100 });
        setAlerts(alertRes?.items || []);
      }

      if (currentTab === "expired") {
        const expiredRes = await warehouseInventoryService.getExpiredStock({
          q: stockQuery,
          limit: 100,
          offset: 0,
        });

        setExpiredRows(expiredRes?.items || []);
      }

      if (currentTab === "orders") {
        const [ordersRes, productsRes] = await Promise.all([
          warehouseInventoryService.listOrders({
            status: orderFilter,
            page: 1,
            limit: 50,
          }),
          warehouseInventoryService.listMasterProducts({
            page: 1,
            limit: 100,
          }),
        ]);

        setOrders(ordersRes?.items || []);
        setMasterProducts(productsRes?.items || []);
      }

      if (currentTab === "inbound") {
        const res = await warehouseInventoryService.listInboundReceipts({
          status: receiptFilter,
          page: 1,
          limit: 50,
        });
        setReceipts(res?.items || []);
      }
    } catch (err) {
      setPageError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function refreshCurrentTab() {
    await loadTabData(tab);
  }


  async function updateStockDisplayStatus(item, status) {
    const productId = item?.product_id || item?.productId;
    const productVariantId = item?.product_variant_id || item?.productVariantId || item?.variant_id || item?.variantId;
    const key = getStockRowKey(item);

    try {
      if (!productId || !productVariantId) {
        throw new Error("Product variant missing for this stock row");
      }

      setStockDisplayStatusSavingKey(key);
      setPageError("");

      const res = await warehouseInventoryService.updateStockDisplayStatus({
        productId,
        productVariantId,
        status,
      });

      const nextStatus = res?.item?.stock_display_status || status;

      setStock((prev) =>
        prev.map((row) =>
          getStockRowKey(row) === key
            ? {
              ...row,
              stock_display_status: nextStatus,
              stockDisplayStatus: nextStatus,
            }
            : row
        )
      );
    } catch (err) {
      setPageError(getErrorMessage(err));
    } finally {
      setStockDisplayStatusSavingKey("");
    }
  }


  async function markWarehouseStockZero(item, reason) {
    const productId = item?.product_id || item?.productId;
    const productVariantId = item?.product_variant_id || item?.productVariantId || item?.variant_id || item?.variantId;
    const key = getStockRowKey(item);
    const finalReason = String(reason || "").trim();

    try {
      if (!productId || !productVariantId) {
        throw new Error("Product variant missing for this stock row");
      }

      if (!finalReason || finalReason.length < 3) {
        throw new Error("Reason is required to mark stock zero");
      }

      setStockZeroSavingKey(key);
      setPageError("");

      const res = await warehouseInventoryService.markStockZero({
        productId,
        productVariantId,
        reason: finalReason,
        adjustmentType: "SPOILED",
      });

      const nextItem = res?.item || {};
      const qtyZeroed = Number(res?.qtyZeroed || 0);

      setStock((prev) =>
        prev.map((row) => {
          if (getStockRowKey(row) !== key) return row;

          const currentAvailable = Number(row.qty_available || row.qtyAvailable || 0);
          const currentDamaged = Number(row.qty_damaged || row.qtyDamaged || 0);
          const nextDamaged = currentDamaged + (qtyZeroed || currentAvailable);

          return {
            ...row,
            qty_available: 0,
            qtyAvailable: 0,
            qty_damaged: nextDamaged,
            qtyDamaged: nextDamaged,
            stock_display_status: nextItem.stock_display_status || "OUT_OF_STOCK",
            stockDisplayStatus: nextItem.stock_display_status || "OUT_OF_STOCK",
            manual_out_of_stock: true,
            manualOutOfStock: true,
            manual_out_of_stock_reason: nextItem.manual_out_of_stock_reason || finalReason,
            manualOutOfStockReason: nextItem.manual_out_of_stock_reason || finalReason,
            manual_out_of_stock_at: nextItem.manual_out_of_stock_at || new Date().toISOString(),
            manualOutOfStockAt: nextItem.manual_out_of_stock_at || new Date().toISOString(),
            batches: Array.isArray(row.batches)
              ? row.batches.map((batch) => {
                  const batchAvailable = Number(batch.qty_available || batch.qtyAvailable || 0);
                  const batchDamaged = Number(batch.qty_damaged || batch.qtyDamaged || 0);
                  return {
                    ...batch,
                    qty_available: 0,
                    qtyAvailable: 0,
                    qty_damaged: batchDamaged + batchAvailable,
                    qtyDamaged: batchDamaged + batchAvailable,
                  };
                })
              : row.batches,
          };
        })
      );
    } catch (err) {
      setPageError(getErrorMessage(err));
    } finally {
      setStockZeroSavingKey("");
    }
  }

  function getEditableReceiptNote(note) {
    const raw = String(note || "").trim();

    if (/^WPO:/i.test(raw)) {
      return "";
    }

    return raw;
  }

  async function openOrderDetail(id) {
    try {
      setLoading(true);
      setPageError("");
      const res = await warehouseInventoryService.getOrder(id);
      setOrderDetail(res?.order || null);
      setOrderDetailOpen(true);
    } catch (err) {
      setPageError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function updateDraftItem(index, key, value) {
    setOrderDraftItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        if (key === "productId") {
          const product = masterProducts.find((x) => String(x.id) === String(value));
          const defaultVariant =
            product?.defaultVariant ||
            product?.variants?.find((x) => x?.isDefault) ||
            product?.variants?.[0] ||
            null;

          return {
            ...item,
            productId: value,
            productVariantId: defaultVariant?.id || "",
          };
        }

        return {
          ...item,
          [key]: key === "qty" ? Math.max(1, Number(value || 1)) : value,
        };
      })
    );
  }

  function addDraftItem() {
    setOrderDraftItems((prev) => [...prev, { productId: "", productVariantId: "", qty: 1 }]);
  }

  function removeDraftItem(index) {
    setOrderDraftItems((prev) => prev.filter((_, i) => i !== index));
  }

  async function submitOrderCreate() {
    try {
      setLoading(true);
      setOrderCreateMessage("");

      const items = orderDraftItems
        .map((x) => ({
          productId: x.productId,
          productVariantId: x.productVariantId || undefined,
          qty: Number(x.qty || 0),
        }))
        .filter((x) => x.productId && x.qty > 0);

      if (!items.length) {
        throw new Error("Add at least one valid product row");
      }

      await warehouseInventoryService.createOrder({
        notes: orderNotes.trim() || null,
        items,
      });

      setOrderCreateMessage("Order request created successfully");
      setOrderCreateOpen(false);
      setOrderNotes("");
      setOrderDraftItems([{ productId: "", productVariantId: "", qty: 1 }]);
      await loadTabData("orders");
    } catch (err) {
      setOrderCreateMessage(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function openReceipt(id) {
    try {
      setLoading(true);
      setPageError("");
      const res = await warehouseInventoryService.getInboundReceipt(id);
      const receipt = res?.receipt || null;

      setReceiptDetail(receipt);

      setReceiveForm({
        note: getEditableReceiptNote(receipt?.note),
        items: (receipt?.items || []).map((item) => {
          const qty = Number(item.qty || 0) || 1;
          const damagedQty = Number(item.qty_damaged || item.damaged_qty || 0);

          const receivedQtyRaw =
            item.received_qty ?? item.qty_received ?? item.qtyReceived;

          const receivedQty =
            receivedQtyRaw !== undefined && receivedQtyRaw !== null
              ? Number(receivedQtyRaw || 0)
              : Math.max(0, qty - damagedQty);

          return {
            receiptItemId: item.id,
            productName: item.name || item.productName || "",
            sku: item.sku || "",
            expectedQty: qty,
            splits: [
              {
                qty,
                receivedQty,
                damagedQty,
                reservedQty: 0,
                batchNo: item.batchNo || item.batch_no || "",
                mfgDate: toInputDate(item.mfgDate || item.mfg_date),
                expiryDate: toInputDate(item.expiryDate || item.expiry_date),
                uom: item.uom || "pcs",
              },
            ],
          };
        }),
      });

      setReceiptMessage("");
      setReceiptPopup(null);
      setReceiptOpen(true);
    } catch (err) {
      setPageError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function updateReceiveNote(value) {
    setReceiveForm((prev) => ({ ...prev, note: value }));
  }

  function addSplit(itemIndex) {
    setReceiveForm((prev) => ({
      ...prev,
      items: prev.items.map((item, idx) =>
        idx === itemIndex
          ? {
            ...item,
            splits: [
              ...item.splits,
              {
                qty: 1,
                receivedQty: 1,
                damagedQty: 0,
                reservedQty: 0,
                batchNo: "",
                mfgDate: "",
                expiryDate: "",
                uom: item.splits?.[0]?.uom || "pcs",
              },
            ],
          }
          : item
      ),
    }));
  }

  function removeSplit(itemIndex, splitIndex) {
    setReceiveForm((prev) => ({
      ...prev,
      items: prev.items.map((item, idx) =>
        idx === itemIndex
          ? {
            ...item,
            splits: item.splits.filter((_, sIdx) => sIdx !== splitIndex),
          }
          : item
      ),
    }));
  }

  function updateSplit(itemIndex, splitIndex, key, value) {
    if (key === "qty") return;

    setReceiveForm((prev) => ({
      ...prev,
      items: prev.items.map((item, idx) =>
        idx === itemIndex
          ? {
            ...item,
            splits: item.splits.map((split, sIdx) =>
              sIdx === splitIndex
                ? {
                  ...split,
                  [key]:
                    key === "qty"
                      ? value === ""
                        ? ""
                        : Math.max(1, Number(value || 1))
                      : ["receivedQty", "damagedQty"].includes(key)
                        ? value === ""
                          ? ""
                          : Math.max(0, Number(value || 0))
                        : value,
                }
                : split
            ),
          }
          : item
      ),
    }));
  }

  function makeLocalDate(value) {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function getTodayInputDate() {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  function normalizeInboundReceiveError(message) {
    const raw = String(message || "");
    const lower = raw.toLowerCase();

    if (
      lower.includes("product_batches_warehouse_id_product_id_batch_no_key") ||
      lower.includes("duplicate key value violates unique constraint") ||
      lower.includes("warehouse_id_product_id_batch_no")
    ) {
      return "This batch number already exists for this product in this warehouse. Please use a different Batch No, or merge/update the existing batch instead of creating a duplicate.";
    }

    if (
      lower.includes("chk_batches_exp_after_mfg") ||
      lower.includes("exp_after_mfg") ||
      (lower.includes("expiry") && lower.includes("mfg"))
    ) {
      return "Expiry Date must be after MFG Date. Do not select an expiry date before or same as the manufacturing date.";
    }

    if (
      lower.includes("receivedqty + reservedqty + damagedqty must equal qty") ||
      lower.includes("received + damaged")
    ) {
      return "Received + Damaged must be exactly equal to Qty.";
    }

    if (lower.includes("batch") && lower.includes("required")) {
      return "Batch No is required. Please enter a valid batch number before receiving stock.";
    }

    return raw || "Failed to receive stock. Please check receiving details and try again.";
  }

  function getReceiveValidationErrors() {
    const errors = [];
    const batchTracker = new Map();

    for (const [itemIndex, item] of (receiveForm.items || []).entries()) {
      const itemName = item.productName || `Item ${itemIndex + 1}`;
      const expectedQty = Number(item.expectedQty || 0);
      const splits = Array.isArray(item.splits) ? item.splits : [];

      if (!splits.length) {
        errors.push(`${itemName}: add at least one split row.`);
        continue;
      }

      let totalSplitQty = 0;

      for (const [splitIndex, split] of splits.entries()) {
        const rowName = `${itemName} - split ${splitIndex + 1}`;
        const qty = Number(split.qty || 0);
        const receivedQty = Number(split.receivedQty || 0);
        const damagedQty = Number(split.damagedQty || 0);
        const mfgDate = makeLocalDate(split.mfgDate);
        const expiryDate = makeLocalDate(split.expiryDate);
        const today = makeLocalDate(getTodayInputDate());

        totalSplitQty += qty;

        if (!Number.isFinite(qty) || qty <= 0) {
          errors.push(`${rowName}: Qty must be greater than 0.`);
        }

        if (!Number.isFinite(receivedQty) || receivedQty < 0) {
          errors.push(`${rowName}: Received Qty cannot be negative.`);
        }

        if (!Number.isFinite(damagedQty) || damagedQty < 0) {
          errors.push(`${rowName}: Damaged Qty cannot be negative.`);
        }

        if (Math.abs(receivedQty + damagedQty - qty) > 0.0001) {
          errors.push(`${rowName}: Received + Damaged must be exactly equal to Qty.`);
        }

        if (!String(split.batchNo || "").trim()) {
          errors.push(`${rowName}: Batch No is required.`);
        }

        const batchNo = String(split.batchNo || "").trim();

        if (batchNo && batchNo.length < 5) {
          errors.push(`${rowName}: Batch No must be at least 5 characters.`);
        }

        const cleanBatchNo = String(split.batchNo || "").trim().toLowerCase();
        const productKey = `${String(item.sku || item.productName || itemIndex).trim().toLowerCase()}__${cleanBatchNo}`;

        if (cleanBatchNo) {
          if (batchTracker.has(productKey)) {
            errors.push(
              `${rowName}: Batch No "${split.batchNo}" is already used for this same product. Please merge the quantity or use a different batch number.`
            );
          } else {
            batchTracker.set(productKey, true);
          }
        }

        if (!split.mfgDate) {
          errors.push(`${rowName}: MFG Date is required.`);
        } else if (!mfgDate) {
          errors.push(`${rowName}: MFG Date is invalid.`);
        }

        if (!split.expiryDate) {
          errors.push(`${rowName}: Expiry Date is required.`);
        } else if (!expiryDate) {
          errors.push(`${rowName}: Expiry Date is invalid.`);
        }

        if (mfgDate && today && mfgDate.getTime() > today.getTime()) {
          errors.push(`${rowName}: MFG Date cannot be a future date.`);
        }

        if (mfgDate && expiryDate && expiryDate.getTime() <= mfgDate.getTime()) {
          errors.push(`${rowName}: Expiry Date must be after MFG Date.`);
        }
      }

      if (expectedQty > 0 && totalSplitQty > expectedQty) {
        errors.push(`${itemName}: total split Qty (${totalSplitQty}) cannot be more than Expected Qty (${expectedQty}).`);
      }
    }

    return errors;
  }

  function openReceiptErrorPopup(title, errors) {
    const items = Array.isArray(errors) ? errors.filter(Boolean) : [];
    const message = items[0] || "Please correct the highlighted receiving details.";

    setReceiptMessage(message);
    setReceiptPopup({
      type: "error",
      title,
      message,
      items: items.length > 1 ? items : [],
    });
  }

  async function submitReceive() {
    try {
      setReceiving(true);
      setReceiptMessage("");
      setReceiptPopup(null);

      const validationErrors = getReceiveValidationErrors();
      if (validationErrors.length) {
        openReceiptErrorPopup("Fix receiving details", validationErrors);
        return;
      }

      const payload = {
        note: receiveForm.note?.trim() || null,
        items: receiveForm.items.map((item) => ({
          receiptItemId: item.receiptItemId,
          splits: (item.splits || []).map((split) => ({
            qty: Number(split.qty || 0),
            receivedQty: Number(split.receivedQty || 0),
            damagedQty: Number(split.damagedQty || 0),
            reservedQty: 0,
            batchNo: String(split.batchNo || "").trim(),
            mfgDate: split.mfgDate || null,
            expiryDate: split.expiryDate || null,
            uom: split.uom || "pcs",
          })),
        })),
      };

      await warehouseInventoryService.receiveInboundReceipt(receiptDetail?.id, payload);

      setReceiptMessage("Inbound receipt received successfully");
      setReceiptOpen(false);
      await loadTabData("inbound");

      if (tab === "stock" || tab === "expired" || tab === "alerts") {
        await loadTabData(tab);
      }
    } catch (err) {
      const message = normalizeInboundReceiveError(getErrorMessage(err));
      openReceiptErrorPopup("Receive stock failed", [message]);
    } finally {
      setReceiving(false);
    }
  }

  const processedStock = useMemo(() => processStockItems(stock), [stock]);

  const activeStock = useMemo(
    () => processedStock.filter((item) => Number(item.qty_available || 0) > 0),
    [processedStock]
  );

  const stockRowsForCurrentMode = useMemo(
    () => (
      onlyComingSoon
        ? processedStock.filter((item) => Number(item.qty_available || 0) <= 0)
        : activeStock
    ),
    [processedStock, activeStock, onlyComingSoon]
  );

  const stockFilterOptions = useMemo(() => {
    const { categories, byId: categoryById } = buildCategoryMeta([
      ...(stockLookups.categories || []),
      ...(stockLookups.subcategories || []),
    ]);
    const usedCategoryIds = getUsedCategoryIds(stockRowsForCurrentMode, categoryById);

    // Products are attached to leaf categories, but a warehouse user must be able
    // to filter by any ancestor (department/category/subcategory). Keep every
    // ancestor of an actually-used category visible and show its full path.
    const visibleIds = new Set();
    for (const usedId of usedCategoryIds) {
      let currentId = String(usedId || "");
      const seen = new Set();
      while (currentId && !seen.has(currentId)) {
        seen.add(currentId);
        visibleIds.add(currentId);
        const item = categoryById.get(currentId);
        currentId = String(item ? getParentId(item) : "");
      }
    }

    const categoryOptions = categories
      .filter((item) => !visibleIds.size || visibleIds.has(String(getLookupId(item) || "")))
      .map((item) => ({
        value: getLookupId(item),
        label: categoryPath(getLookupId(item), categoryById) || getLookupLabel(item),
      }))
      .filter((item) => item.value)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));

    const brandOptions = uniqueLookupList(stockLookups.brands)
      .map((item) => ({ value: getLookupId(item), label: getLookupLabel(item) }))
      .filter((item) => item.value)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));

    return { category: categoryOptions, subcategory: [], brand: brandOptions };
  }, [stockLookups, stockRowsForCurrentMode]);

  const filteredActiveStock = useMemo(() => {
    const { categories, byId } = buildCategoryMeta([
      ...(stockLookups.categories || []),
      ...(stockLookups.subcategories || []),
    ]);
    const categoryIds = collectCategoryDescendantIds(categories, stockCategory);
    const categoryNames = categoryIds
      .map((id) => getLookupLabel(byId.get(String(id))))
      .filter(Boolean);

    const brandIds = stockBrand ? [stockBrand] : [];
    const brandNames = stockBrand
      ? [optionLabel(stockFilterOptions.brand, stockBrand)].filter(Boolean)
      : [];

    return stockRowsForCurrentMode.filter((item) => {
      const query = cleanText(debouncedStockQuery);
      if (query) {
        const haystack = cleanText([
          item?.name,
          item?.product_name,
          item?.productName,
          item?.sku,
          item?.variant_sku,
          item?.variantSku,
          item?.barcode,
          item?.category_name,
          item?.categoryName,
          item?.brand_name,
          item?.brandName,
        ].filter(Boolean).join(" "));

        if (!haystack.includes(query)) return false;
      }

      if (onlyLow) {
        const qty = Number(item?.qty_available || 0);
        const threshold = Number(item?.low_stock_threshold || 10);
        if (qty > threshold) return false;
      }

      const categoryOk = matchesByIdsOrNames(item, {
        ids: categoryIds,
        names: categoryNames,
        idKeys: [
          "category_id",
          "categoryId",
          "category",
          "subcategory_id",
          "subCategoryId",
          "subcategoryId",
        ],
        nameKeys: [
          "category_name",
          "categoryName",
          "category_label",
          "categoryLabel",
          "subcategory_name",
          "subCategoryName",
          "subcategoryName",
        ],
      });

      const brandOk = matchesByIdsOrNames(item, {
        ids: brandIds,
        names: brandNames,
        idKeys: ["brand_id", "brandId", "brand"],
        nameKeys: ["brand_name", "brandName", "brand_label", "brandLabel"],
      });

      return categoryOk && brandOk;
    });
  }, [stockRowsForCurrentMode, debouncedStockQuery, onlyLow, stockCategory, stockBrand, stockFilterOptions, stockLookups]);

  function clearStockFilters() {
    setStockQuery("");
    setStockCategory("");
    setStockSubcategory("");
    setStockBrand("");
    setOnlyLow(false);
    setOnlyComingSoon(false);
    setStockPage(1);
  }

  const expiredStock = useMemo(
    () => (Array.isArray(expiredRows) ? expiredRows : []),
    [expiredRows]
  );

  const stats = useMemo(() => {
    const totalAvailable = filteredActiveStock.reduce(
      (sum, item) => sum + Number(item.qty_available || 0),
      0
    );

    const lowItems = filteredActiveStock.filter(
      (item) => Number(item.qty_available || 0) <= Number(item.low_stock_threshold || 10)
    ).length;

    const expiryItems = alerts.filter(
      (item) => String(item.type || item.alert_type || "").toUpperCase() === "EXPIRY_SOON"
    ).length;

    return {
      totalProducts: filteredActiveStock.length,
      totalAvailable,
      lowItems,
      expiryItems,
      expiredProducts: expiredStock.length,
      expiredQty: expiredStock.reduce(
        (sum, item) => sum + Number(item.qty_available || 0),
        0
      ),
    };
  }, [filteredActiveStock, expiredStock, alerts]);

  const stockTotal = Number(filteredActiveStock.length || 0);

  const stockTotalPages = useMemo(() => {
    if (!stockTotal || stockTotal <= 0) return 1;
    return Math.max(1, Math.ceil(stockTotal / stockPageSize));
  }, [stockTotal, stockPageSize]);

  const stockPageSizeOptions = useMemo(() => {
    const base = [10, 25, 50, 100, 250, 500];

    let opts = base.filter((n) => stockTotal > 0 && n <= stockTotal);

    if (stockTotal > 0 && !opts.includes(stockTotal)) opts.push(stockTotal);
    if (stockTotal <= 0) opts = [10, 25, 50, 100, 250, 500];

    return Array.from(new Set(opts)).sort((a, b) => a - b);
  }, [stockTotal]);

  const stockStartIndex =
    stockTotal === 0 ? 0 : (stockPage - 1) * stockPageSize + 1;

  const stockEndIndex =
    stockTotal === 0 ? 0 : Math.min(stockPage * stockPageSize, stockTotal);

  const stockCanPrev = stockPage > 1;
  const stockCanNext = stockPage < stockTotalPages;

  function onChangeStockPageSize(value) {
    const n = Number(value);
    setStockPage(1);
    setStockPageSize(Number.isFinite(n) && n > 0 ? n : 10);
  }
  const alertsTotalPages = Math.max(1, Math.ceil((alerts.length || 0) / PAGE_SIZE));
  const expiredTotalPages = Math.max(1, Math.ceil(expiredStock.length / PAGE_SIZE));
  const ordersTotalPages = Math.max(1, Math.ceil(orders.length / PAGE_SIZE));
  const receiptsTotalPages = Math.max(1, Math.ceil(receipts.length / PAGE_SIZE));

  const paginatedStock = useMemo(() => {
    const start = (stockPage - 1) * stockPageSize;
    return filteredActiveStock.slice(start, start + stockPageSize);
  }, [filteredActiveStock, stockPage, stockPageSize]);

  const paginatedExpiredStock = useMemo(() => {
    const start = (expiredPage - 1) * PAGE_SIZE;
    return expiredStock.slice(start, start + PAGE_SIZE);
  }, [expiredStock, expiredPage]);

  const paginatedOrders = useMemo(() => {
    const start = (ordersPage - 1) * PAGE_SIZE;
    return orders.slice(start, start + PAGE_SIZE);
  }, [orders, ordersPage]);

  const paginatedReceipts = useMemo(() => {
    const start = (receiptsPage - 1) * PAGE_SIZE;
    return receipts.slice(start, start + PAGE_SIZE);
  }, [receipts, receiptsPage]);

  useEffect(() => {
    if (stockPage > stockTotalPages) setStockPage(stockTotalPages);
  }, [stockPage, stockTotalPages]);

  useEffect(() => {
    if (alertsPage > alertsTotalPages) setAlertsPage(alertsTotalPages);
  }, [alertsPage, alertsTotalPages]);

  useEffect(() => {
    if (expiredPage > expiredTotalPages) setExpiredPage(expiredTotalPages);
  }, [expiredPage, expiredTotalPages]);

  useEffect(() => {
    if (ordersPage > ordersTotalPages) setOrdersPage(ordersTotalPages);
  }, [ordersPage, ordersTotalPages]);

  useEffect(() => {
    if (receiptsPage > receiptsTotalPages) setReceiptsPage(receiptsTotalPages);
  }, [receiptsPage, receiptsTotalPages]);

  const isReceiptAlreadyReceived =
    String(receiptDetail?.status || "").toLowerCase() === "received";

  return (
    <div className="space-y-6">
      <InventoryHeader
        onRefresh={refreshCurrentTab}
        borderColor={BORDER}
        title={pageTitle}
        description={pageDescription}
      />

      {showTabs ? (
        <div
          className="rounded-[20px] border bg-white p-6 shadow-[0_12px_30px_rgba(216,106,120,0.08)]"
          style={{ borderColor: BORDER }}
        >
          <InventoryTabs tabs={safeTabs} activeTab={tab} onChange={setTab} />
        </div>
      ) : null}

      {pageError ? (
        <div className="rounded-[16px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-3 text-[14px] font-medium text-[#d86a78]">
          {pageError}
        </div>
      ) : null}

      {tab === "stock" ? (
        <StockSection
          stats={stats}
          stockQuery={stockQuery}
          setStockQuery={setStockQuery}
          includeBatches={includeBatches}
          setIncludeBatches={setIncludeBatches}
          onlyLow={onlyLow}
          setOnlyLow={setOnlyLow}
          onlyComingSoon={onlyComingSoon}
          setOnlyComingSoon={setOnlyComingSoon}
          onUpdateStockDisplayStatus={updateStockDisplayStatus}
          stockDisplayStatusSavingKey={stockDisplayStatusSavingKey}
          onMarkStockZero={markWarehouseStockZero}
          stockZeroSavingKey={stockZeroSavingKey}
          loadStock={() => loadTabData("stock")}
          loading={loading}
          paginatedStock={paginatedStock}
          stockPage={stockPage}
          stockTotalPages={stockTotalPages}
          setStockPage={setStockPage}
          stockTotal={stockTotal}
          stockStartIndex={stockStartIndex}
          stockEndIndex={stockEndIndex}
          stockPageSize={stockPageSize}
          stockPageSizeOptions={stockPageSizeOptions}
          stockCanPrev={stockCanPrev}
          stockCanNext={stockCanNext}
          onChangeStockPageSize={onChangeStockPageSize}
          stockCategory={stockCategory}
          setStockCategory={setStockCategory}
          stockSubcategory={stockSubcategory}
          setStockSubcategory={setStockSubcategory}
          stockBrand={stockBrand}
          setStockBrand={setStockBrand}
          stockFilterOptions={stockFilterOptions}
          stockFilterLoading={stockLookups.loading}
          stockFilterError={stockLookups.error}
          clearStockFilters={clearStockFilters}
        />
      ) : null}

      {tab === "alerts" ? (
        <StockAlertsSection
          alerts={alerts}
          loading={loading}
          alertsPage={alertsPage}
          alertsTotalPages={alertsTotalPages}
          setAlertsPage={setAlertsPage}
        />
      ) : null}

      {tab === "expired" ? (
        <ExpiredSection
          stats={stats}
          stockQuery={stockQuery}
          setStockQuery={setStockQuery}
          loadExpired={() => loadTabData("expired")}
          loading={loading}
          paginatedExpiredStock={paginatedExpiredStock}
          expiredPage={expiredPage}
          expiredTotalPages={expiredTotalPages}
          setExpiredPage={setExpiredPage}
        />
      ) : null}

      {tab === "orders" ? (
        <OrdersSection
          orderFilter={orderFilter}
          setOrderFilter={setOrderFilter}
          loadOrders={() => loadTabData("orders")}
          setOrderCreateOpen={setOrderCreateOpen}
          loading={loading}
          paginatedOrders={paginatedOrders}
          ordersPage={ordersPage}
          ordersTotalPages={ordersTotalPages}
          setOrdersPage={setOrdersPage}
          onOpenOrderDetail={openOrderDetail}
          warehouseInventoryService={warehouseInventoryService}
        />
      ) : null}

      {tab === "inbound" ? (
        <InboundSection
          receiptFilter={receiptFilter}
          setReceiptFilter={setReceiptFilter}
          loadInbound={() => loadTabData("inbound")}
          loading={loading}
          paginatedReceipts={paginatedReceipts}
          receiptsPage={receiptsPage}
          receiptsTotalPages={receiptsTotalPages}
          setReceiptsPage={setReceiptsPage}
          openReceipt={openReceipt}
          warehouseInventoryService={warehouseInventoryService}
        />
      ) : null}

      <OrderDetailModal
        open={orderDetailOpen}
        orderDetail={orderDetail}
        onClose={() => setOrderDetailOpen(false)}
      />

      <CreateOrderModal
        open={orderCreateOpen}
        onClose={() => setOrderCreateOpen(false)}
        orderCreateMessage={orderCreateMessage}
        orderDraftItems={orderDraftItems}
        updateDraftItem={updateDraftItem}
        removeDraftItem={removeDraftItem}
        addDraftItem={addDraftItem}
        masterProducts={masterProducts}
        orderNotes={orderNotes}
        setOrderNotes={setOrderNotes}
        submitOrderCreate={submitOrderCreate}
      />

      <ReceiptDetailModal
        open={receiptOpen}
        receiptDetail={receiptDetail}
        receiptMessage={receiptMessage}
        receiptPopup={receiptPopup}
        closeReceiptPopup={() => setReceiptPopup(null)}
        receiveForm={receiveForm}
        isReceiptAlreadyReceived={isReceiptAlreadyReceived}
        updateReceiveNote={updateReceiveNote}
        addSplit={addSplit}
        removeSplit={removeSplit}
        updateSplit={updateSplit}
        submitReceive={submitReceive}
        receiving={receiving}
        setReceiptOpen={setReceiptOpen}
        warehouseInventoryService={warehouseInventoryService}
      />
    </div>
  );
}